import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { LuxAwardModalDispatchActions, LuxAwardModalDispatchContext } from './LuxAwardModalContexts'
import { PRIZES, RUNNER_UP_PRIZES } from './luxAwardPrizes'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import TextButton from 'LuxKit/Button/TextButton'
import HeroImage from 'content/components/HeroImage'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { getThemeClassName } from 'lib/theme/themeUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'

const GRAND_PRIZES = PRIZES.filter(prize => prize.type === 'Grand Prize')
const HeroContainer = styled.div`
  z-index: 1;
  padding: ${rem(32)} ${rem(20)};
  display: grid;
  place-items: center;
  background-color: rgba(0,0,0,0.3);
  width: 100%;
  height: 100%;

  ${mediaQueryUp.tablet} {
    padding: ${rem(48)} ${rem(120)};
  }
`

const StyledHeroImage = styled(HeroImage)`
  padding: 0;
`

interface Props {
  exploreUrl: string
  activeRegionCode: string
}

function LuxAwardModalStepHome({ exploreUrl, activeRegionCode }: Props) {
  const intl = useIntl()
  const dispatch = useContext(LuxAwardModalDispatchContext)

  const runnerUpPrize = useMemo(() => RUNNER_UP_PRIZES[activeRegionCode], [activeRegionCode])

  const handlePrizeViewClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const step = String(event.currentTarget.dataset.prizekey)
    dispatch({ type: LuxAwardModalDispatchActions.GO_TO, step })
  }, [dispatch])

  const prizesText = intl.formatMessage({
    id: 'mC6YU5WXwux6r3KgUk3w',
    defaultMessage: 'Prizes',
    description: 'Heading for the prizes section in the Lux Award modal',
  })

  const prizesDescriptionText = intl.formatMessage({
    id: 'SRzB5nca20lASRCH1A7T',
    defaultMessage: 'Vote for your favourite escapes and you’ll be in the running to win incredible prizes, including a year of Luxury Escapes!',
    description: 'Description text encouraging users to vote for escapes to win prizes in the Lux Award modal',
  })

  const grandPrizeText = intl.formatMessage({
    id: 'LrF64cbBlcYaFr6km0Bi',
    defaultMessage: 'Grand Prize',
    description: 'Heading for the grand prize section in the Lux Award modal',
  })

  const grandPrizeDescriptionText = intl.formatMessage({
    id: '8vnm7Ca1HxxkghP4rAWE',
    defaultMessage: 'A year of Luxury Escapes, with six retreats to enjoy throughout 2025*',
    description: 'Description of the grand prize in the Lux Award modal',
  })

  const viewPrizeText = intl.formatMessage({
    id: 'SYrVixDjsXsGhanCT0WR',
    defaultMessage: 'View prize',
    description: 'Button text to view prize details in the Lux Award modal',
  })

  const runnerUpPrizesText = intl.formatMessage({
    id: 'juOc_2ZYKlP6DjNu-ILN',
    defaultMessage: 'Runner-Up Prizes',
    description: 'Heading for the runner-up prizes section in the Lux Award modal',
  })

  const exploreEscapesText = intl.formatMessage({
    id: 'N115ilvCX7R8BnjfBcfN',
    defaultMessage: 'Explore escapes',
    description: 'Primary action button text to explore available escapes in the Lux Award modal',
  })

  return <>
    <ModalHeader
      title=""
    />
    <ModalBody>
      <StyledHeroImage
        id="q7eqq1113a534wwvj2xm"
        aspectRatio="3.15"
        className={getThemeClassName({ mobile: 'inverse' })}
      >
        <HeroContainer>
          <Group direction="vertical" gap={8} horizontalAlign="center">
            <HeadingTextBlock variant="heading2alt" colour="neutral-one" align="center">{prizesText}</HeadingTextBlock>
            <BodyTextBlock variant="medium" colour="neutral-one" align="center">{prizesDescriptionText}</BodyTextBlock>
          </Group>
        </HeroContainer>
      </StyledHeroImage>
      <ModalContent>
        <Group direction="vertical" gap={20}>
          <Group direction="vertical">
            <HeadingTextBlock variant="heading3">{grandPrizeText}</HeadingTextBlock>
            <BodyText variant="medium">{grandPrizeDescriptionText}</BodyText>
            {GRAND_PRIZES.map((prize) => <ClickableListItem key={prize.key} data-prizekey={prize.key} title={prize.title} startIcon={<ResponsiveImage height={48} width={48} aspectRatio="1:1" id={prize.image.id!} />} onClick={handlePrizeViewClick}>
              <TextButton kind="tertiary" size="medium" nonInteractable>{viewPrizeText}</TextButton>
            </ClickableListItem>)}
          </Group>
          <Group direction="vertical">
            <HeadingTextBlock variant="heading3">{runnerUpPrizesText}</HeadingTextBlock>
            <ClickableListItem key="runner-up-prize" data-prizekey="runner-up-prize" title={runnerUpPrize}>
            </ClickableListItem>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: exploreEscapesText,
        to: exploreUrl,
      }}
    />
  </>
}

export default LuxAwardModalStepHome
